import React from 'react';
import { useIntl } from 'react-intl';

/** One chip in the heat-strip. */
export interface RpeChip {
  /** Text rendered on the chip and value committed when selected. */
  label: string;
  /** Numeric value, used to pick a fallback tint when no override is supplied. */
  numeric: number;
  /** Optional backend-provided color (`RpeValueMetaDto.color`), with or without `#`. */
  colorOverride?: string | null;
}

export interface RpeHeatStripProps {
  values: RpeChip[];
  selected: string | null | undefined;
  onSelect: (label: string) => void;
  showLabel?: boolean;
}

/**
 * v2 RPE picker — single-tap heat-strip of color-tinted chips. Replaces the
 * legacy Menu dropdown. See design handoff §4.6.
 *
 * Tint rubric (fallback when `colorOverride` is absent):
 *   value < 6   → success
 *   value < 7.5 → lime ink
 *   value < 9   → warning orange
 *   value >= 9  → danger
 */
const RpeHeatStrip: React.FC<RpeHeatStripProps> = ({ values, selected, onSelect, showLabel = true }) => {
  const intl = useIntl();
  return (
    <div className="et-rpe-strip">
      {showLabel && <span className="et-rpe-strip__label">{intl.formatMessage({ id: 'session.rpe.label' })}</span>}
      <div className="et-rpe-strip__row">
        {values.map((chip) => {
          const isSelected = selected === chip.label;
          const tint = resolveTint(chip.numeric, chip.colorOverride);
          const style: React.CSSProperties = isSelected
            ? { background: tint, borderColor: tint, color: '#fff' }
            : { background: addAlpha(tint, 0.14), borderColor: addAlpha(tint, 0.4), color: tint };
          return (
            <button
              key={chip.label}
              type="button"
              className={`et-rpe-strip__chip${isSelected ? ' is-selected' : ''}`}
              style={style}
              onClick={() => onSelect(chip.label)}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Returns a hex color (with `#` prefix). */
function resolveTint(value: number, override?: string | null): string {
  if (override) {
    const clean = override.startsWith('#') ? override : `#${override}`;
    if (/^#[0-9a-fA-F]{6}$/.test(clean) || /^#[0-9a-fA-F]{8}$/.test(clean)) {
      return clean;
    }
  }
  if (value < 6) return '#22C55E';
  if (value < 7.5) return '#4D7C0F';
  if (value < 9) return '#F97316';
  return '#EF4444';
}

/** Adds an alpha channel to a 6-char hex. Falls back to the original on invalid input. */
function addAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${m[1]}${a}`;
}

export default RpeHeatStrip;
